<template>
    <div class="mt-8 border border-[#E3E5E6] rounded-lg p-4 md:p-6 bg-white dark:bg-dark-secondary">
        <h5 class="font-semibold text-lg md:text-xl">Pagar con Wompi (Panamá)</h5>
        <p class="text-sm text-gray-600 mt-2">
            Moneda: USD. Total a pagar:
            <span class="font-semibold">{{ formatUsd(amountInCents) }}</span>
        </p>

        <div v-if="backendError" class="mt-4 rounded border border-red-300 bg-red-50 text-red-700 px-4 py-3">
            {{ backendError }}
        </div>

        <div
            v-if="paymentStatus"
            class="mt-4 rounded border px-4 py-3"
            :class="statusCssClass"
        >
            <div class="font-semibold">{{ statusLabel }}</div>
            <div class="mt-1 flex items-center gap-2" v-if="paymentStatus === 'PENDING'">
                <span class="inline-block h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                <span>{{ uiMessage || 'Estamos confirmando tu pago...' }}</span>
            </div>
            <div class="mt-1" v-else>
                {{ uiMessage }}
            </div>
            <div v-if="errorReason" class="mt-2 text-sm">
                Motivo: {{ errorReason }}
            </div>
        </div>

        <div class="mt-4 flex flex-wrap gap-3">
            <button
                type="button"
                class="btn btn-theme-solid checkout-action-btn"
                @click="startPayment"
                :disabled="isLoading"
            >
                <span>{{ isLoading ? 'Preparando pago...' : 'Pagar con Wompi (Panamá)' }}</span>
            </button>

            <button
                v-if="canRetry"
                type="button"
                class="btn btn-outline"
                @click="startPayment"
            >
                <span>Reintentar</span>
            </button>
        </div>

        <div ref="widgetContainer" class="mt-4"></div>

        <a
            v-if="paymentStatus === 'APPROVED'"
            :href="continueUrl"
            class="inline-block mt-4 text-primary underline"
        >
            Continuar
        </a>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'WompiPanamaCheckout',
    props: {
        amountInCents: {
            type: Number,
            required: true,
        },
        customerEmail: {
            type: String,
            default: '',
        },
        emailInputSelector: {
            type: String,
            default: 'input[name="billing[email]"]',
        },
        orderId: {
            type: [Number, String],
            default: null,
        },
        redirectUrl: {
            type: String,
            required: true,
        },
        initEndpoint: {
            type: String,
            default: '/api/checkout/wompi-pa/init',
        },
        statusEndpoint: {
            type: String,
            default: '/api/checkout/wompi-pa/status',
        },
        widgetUrl: {
            type: String,
            default: 'https://checkout.wompi.pa/widget.js',
        },
        continueUrl: {
            type: String,
            default: '/',
        },
        appEnv: {
            type: String,
            default: 'production',
        },
    },
    data() {
        return {
            isLoading: false,
            backendError: null,
            paymentStatus: null,
            statusLabel: '',
            statusSeverity: 'info',
            uiMessage: '',
            errorReason: null,
            pollingTimer: null,
            latestQuery: null,
        };
    },
    computed: {
        canRetry() {
            return ['DECLINED', 'VOIDED', 'ERROR'].includes(this.paymentStatus);
        },
        statusCssClass() {
            const map = {
                success: 'border-green-300 bg-green-50 text-green-700',
                warning: 'border-yellow-300 bg-yellow-50 text-yellow-800',
                danger: 'border-red-300 bg-red-50 text-red-700',
                info: 'border-blue-300 bg-blue-50 text-blue-700',
            };

            return map[this.statusSeverity] || map.info;
        },
    },
    mounted() {
        const params = new URLSearchParams(window.location.search);
        const wompiTransactionId = params.get('id');
        const reference = params.get('reference');

        if (wompiTransactionId) {
            this.fetchStatus({ id: wompiTransactionId });
            return;
        }

        if (reference) {
            this.fetchStatus({ reference });
        }
    },
    beforeUnmount() {
        this.stopPolling();
    },
    methods: {
        async startPayment(attempt = 0) {
            this.isLoading = true;
            this.backendError = null;
            this.errorReason = null;
            this.setStatus('PENDING', 'Pendiente', 'info', 'Abriendo pasarela...');

            const customerEmail = this.resolveCustomerEmail();
            if (!customerEmail) {
                this.isLoading = false;
                this.backendError = 'Debes ingresar un correo válido para continuar.';
                this.setStatus('ERROR', 'Error', 'danger', 'No se pudo iniciar el pago.');
                return;
            }

            try {
                const payload = {
                    amount_in_cents: this.amountInCents,
                    customer_email: customerEmail,
                    redirect_url: this.redirectUrl,
                };

                if (this.orderId !== null && this.orderId !== '') {
                    payload.order_id = this.orderId;
                }

                const { data } = await axios.post(this.initEndpoint, payload);
                this.latestQuery = { reference: data.reference };
                this.renderWidget(data);
                this.fetchStatus(this.latestQuery, true);
            } catch (error) {
                const response = error?.response;

                if (this.shouldRetryReference(response) && attempt === 0) {
                    this.logDebugError(error);
                    await this.startPayment(1);
                    return;
                }

                this.handleApiError(error, 'No pudimos iniciar el pago.');
                this.setStatus('ERROR', 'Error', 'danger', 'No se pudo iniciar el pago.');
            } finally {
                this.isLoading = false;
            }
        },
        async fetchStatus(query, isPolling = false) {
            if (!isPolling) {
                this.backendError = null;
            }

            this.latestQuery = query;

            try {
                const { data } = await axios.get(this.statusEndpoint, { params: query });
                this.applyStatusResponse(data);

                if (data.is_final) {
                    this.stopPolling();
                } else {
                    this.startPolling();
                }
            } catch (error) {
                this.stopPolling();
                this.handleApiError(error, 'No pudimos verificar el estado del pago.');
            }
        },
        renderWidget(config) {
            const container = this.$refs.widgetContainer;
            if (!container) {
                return;
            }

            container.innerHTML = '';

            const form = document.createElement('form');
            const script = document.createElement('script');
            script.src = this.widgetUrl;
            script.async = true;
            script.setAttribute('data-render', 'button');
            script.setAttribute('data-public-key', config.public_key);
            script.setAttribute('data-currency', config.currency || 'USD');
            script.setAttribute('data-amount-in-cents', String(config.amount_in_cents));
            script.setAttribute('data-reference', config.reference);
            script.setAttribute('data-signature:integrity', config.signature_integrity);

            if (config.redirect_url) {
                script.setAttribute('data-redirect-url', config.redirect_url);
            }

            form.appendChild(script);
            container.appendChild(form);

            setTimeout(() => {
                const widgetButton = container.querySelector('button');
                if (widgetButton) {
                    widgetButton.click();
                }
            }, 700);
        },
        applyStatusResponse(data) {
            const status = data.status || 'PENDING';
            this.setStatus(
                status,
                data.ui_label || status,
                data.ui_severity || 'info',
                data.ui_message || 'Estamos confirmando tu pago...'
            );

            this.errorReason = data.error_reason || null;
        },
        setStatus(status, label, severity, message) {
            this.paymentStatus = status;
            this.statusLabel = label;
            this.statusSeverity = severity;
            this.uiMessage = message;
        },
        startPolling() {
            if (this.pollingTimer || !this.latestQuery) {
                return;
            }

            this.pollingTimer = setInterval(() => {
                this.fetchStatus(this.latestQuery, true);
            }, 5000);
        },
        stopPolling() {
            if (!this.pollingTimer) {
                return;
            }

            clearInterval(this.pollingTimer);
            this.pollingTimer = null;
        },
        handleApiError(error, fallbackMessage) {
            const response = error?.response;
            const status = response?.status;
            const reason = response?.data?.error?.reason || fallbackMessage;

            if (status === 401) {
                this.backendError = 'No autorizado para realizar esta operación.';
            } else if (status === 422) {
                this.backendError = reason || 'Revisa los datos del pago e inténtalo nuevamente.';
            } else if (status >= 500) {
                this.backendError = 'Ocurrió un error interno. Intenta de nuevo en unos minutos.';
            } else {
                this.backendError = reason || fallbackMessage;
            }

            this.logDebugError(error);
        },
        shouldRetryReference(response) {
            if (!response || response.status !== 422) {
                return false;
            }

            const code = response?.data?.error?.code || '';
            const reason = response?.data?.error?.reason || '';

            return code === 'REFERENCE_ALREADY_USED' || /referencia.*usada/i.test(reason);
        },
        resolveCustomerEmail() {
            const input = document.querySelector(this.emailInputSelector);
            const value = input?.value?.trim();

            return value || this.customerEmail || '';
        },
        formatUsd(amountInCents) {
            return new Intl.NumberFormat('es-PA', {
                style: 'currency',
                currency: 'USD',
            }).format((amountInCents || 0) / 100);
        },
        logDebugError(error) {
            if (this.appEnv !== 'production') {
                // Solo visible en desarrollo para depuración.
                console.error('[Wompi Panamá]', error);
            }
        },
    },
};
</script>
